import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { DropdownMenuShortcut,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Plus, Briefcase, Users } from 'lucide-react'
import { usePermissions } from '@/hooks/usePermissions'
import { JobWizard } from '@/components/jobs/JobWizard'
import { CandidateFormSheet } from '@/components/candidates/CandidateFormSheet'
import { DuplicateFlowDialog } from '@/components/candidates/duplicate/DuplicateFlowDialog'
import { useIndependentCandidates, CreateIndependentCandidateData, IndependentCandidate } from '@/hooks/useIndependentCandidates'
import { supabase } from '@/lib/supabaseClient'
import { toast } from '@/hooks/use-toast'

export function GlobalCreateButton() {
  const navigate = useNavigate()
  const { canCreateJobs, canCreateCandidates } = usePermissions()
  const [jobWizardOpen, setJobWizardOpen] = useState(false)
  const [candidateSheetOpen, setCandidateSheetOpen] = useState(false)
  const [showMergeDialog, setShowMergeDialog] = useState(false)
  const [duplicateInfo, setDuplicateInfo] = useState<{
    existing: any
    incoming: any
    merged: any
    assignedJobId?: string
    assignedStageId?: string
  } | null>(null)
  const { addCandidate, updateCandidate, isLoading: isCreatingCandidate } = useIndependentCandidates()

  // Handle candidate submission with job assignment logic
  const handleCandidateSubmit = async (candidateData: CreateIndependentCandidateData & { assignedJobId?: string; assignedStageId?: string }) => {
    try {
      // Create the candidate first
      const result = await addCandidate(candidateData)
      
      // Check if duplicate was detected
      if (result && 'isDuplicate' in result) {
        // Show merge dialog
        setDuplicateInfo({
          existing: result.existingCandidate,
          incoming: result.incomingData,
          merged: result.mergedData,
          assignedJobId: candidateData.assignedJobId,
          assignedStageId: candidateData.assignedStageId
        })
        setShowMergeDialog(true)
        return
      }
      
      const newCandidate = result as IndependentCandidate
      
      // If job is assigned, create the job-candidate association
      if (candidateData.assignedJobId && newCandidate && newCandidate.id) {
        // Check if association already exists
        const { data: existingAssoc } = await supabase
          .from('job_candidate_associations')
          .select('id')
          .eq('job_id', candidateData.assignedJobId)
          .eq('candidate_id', newCandidate.id)
          .maybeSingle()
        
        if (existingAssoc) {
          toast({
            title: 'Success',
            description: 'Candidate created and assigned to job successfully!'
          })
        } else {
          // Create new association
          const { error: associationError } = await supabase
            .from('job_candidate_associations')
            .insert({
              job_id: candidateData.assignedJobId,
              candidate_id: newCandidate.id,
              current_stage_id: candidateData.assignedStageId || null,
              status: 'active',
              added_by: (await supabase.auth.getUser()).data.user?.id
            })

          if (associationError) {
            console.error('Error creating job association:', associationError)
            toast({
              title: 'Warning',
              description: 'Candidate created but could not be assigned to job. You can assign them manually.',
              variant: 'destructive'
            })
          } else {
            toast({
              title: 'Success',
              description: 'Candidate created and assigned to job successfully!'
            })
          }
        }
        // Navigate to the job page and open the candidate profile
        navigate(`/jobs/${candidateData.assignedJobId}?candidate=${newCandidate.id}`)
      } else {
        toast({
          title: 'Success',
          description: 'Candidate created successfully!'
        })
        // Navigate to candidates page and open the candidate profile sheet
        if (newCandidate?.id) {
          navigate(`/candidates?openCandidate=${newCandidate.id}`)
        } else {
          navigate('/candidates')
        }
      }
      
      setCandidateSheetOpen(false)
      return newCandidate
    } catch (error) {
      console.error('Error creating candidate:', error)
      // Error handling is done in the hook
    }
  }

  const handleDuplicateResolved = (candidateId: string) => {
    setShowMergeDialog(false)
    setDuplicateInfo(null)
    setCandidateSheetOpen(false)
    navigate(`/candidates?openCandidate=${candidateId}`)
  }

  // Handle keyboard shortcuts
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey) {
        if (e.key === 'j' && canCreateJobs) {
          e.preventDefault()
          setJobWizardOpen(true)
        } else if (e.key === 'k' && canCreateCandidates) {
          e.preventDefault()
          setCandidateSheetOpen(true)
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [canCreateJobs, canCreateCandidates])

  // Don't render if user has no create permissions
  if (!canCreateJobs && !canCreateCandidates) {
    return null
  }

  const createOptions = []

  if (canCreateJobs) {
    createOptions.push({
      label: 'New Job',
      description: 'Create a new job posting',
      icon: Briefcase,
      onClick: () => setJobWizardOpen(true),
      shortcut: '⌘J'
    })
  }

  if (canCreateCandidates) {
    createOptions.push({
      label: 'New Candidate',
      description: 'Add a new candidate',
      icon: Users,
      onClick: () => setCandidateSheetOpen(true),
      shortcut: '⌘K'
    })
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="Create"
            className="inline-flex items-center gap-1.5 h-7 pl-2 pr-2.5 rounded-lg bg-white text-[#0d0d09] font-poppins font-semibold text-[12.5px] tracking-[-0.01em] hover:bg-[#FAFAF7] active:bg-[#F1F0EC] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-virgilio-purple/40"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>Create</span>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-60">
          {createOptions.map((option) => {
            const Icon = option.icon
            return (
              <DropdownMenuItem
                key={option.label}
                onClick={option.onClick}
              >
                <Icon className="h-3.5 w-3.5 text-[hsl(var(--menu-group-color))]" />
                <span className="flex-1 truncate">{option.label}</span>
                <DropdownMenuShortcut>{option.shortcut}</DropdownMenuShortcut>
              </DropdownMenuItem>
            )
          })}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Job Wizard */}
      <JobWizard
        isOpen={jobWizardOpen}
        onClose={() => setJobWizardOpen(false)}
      />

      {/* Candidate Form Sheet */}
      <CandidateFormSheet
        isOpen={candidateSheetOpen}
        onClose={() => setCandidateSheetOpen(false)}
        onSubmit={handleCandidateSubmit}
        isLoading={isCreatingCandidate}
      />

      {/* Merge Dialog */}
      {duplicateInfo && (
        <DuplicateFlowDialog
          isOpen={showMergeDialog}
          existingCandidate={duplicateInfo.existing}
          incoming={duplicateInfo.incoming}
          jobId={duplicateInfo.assignedJobId ?? null}
          stageId={duplicateInfo.assignedStageId ?? null}
          onCancel={() => {
            setShowMergeDialog(false)
            setDuplicateInfo(null)
          }}
          onResolved={handleDuplicateResolved}
        />
      )}
    </>
  )
}